import type { LifecycleStatusCardData } from '../components/PieChartCard/PieChartCard';

export interface ChartDataPoint {
  name: string;
  count: number;
}

export interface DeviceTypeData {
  device_type: string;
  count: number;
}

export interface DeviceTypeCategoryData {
  category: string;
  total_count: number;
  device_types: DeviceTypeData[];
}

export const chartPalette = {
  // Category colors
  categories: {
    'Asset Management': '#339FFF',  // Light Blue (updated to match device type color)  
    'Measurement Instrumentation': '#014C8E',  // Blue
    'Valves, Actuators, and Regulators': '#1C2653',  // Dark Navy Blue
    'Fluid Control & Pnuematics': '#F79428',  // Orange
    'Control & Safety Systems': '#FEC944',  // Yellow
    'Process Control': '#F79428',  // Orange
    'Safety Systems': '#FEC944',  // Yellow
    'Other': '#9E9E9E'  // Gray
  },
  // Device type color palettes by category (matching DeviceList)
  deviceTypePalettes: {
    'Measurement Instrumentation': [
      "#003564", "#2A575F", "#0076DF", "#E30A7C", "#89C1CB", "#950751",
      "#319FFF", "#438A98", "#6E298D", "#0061B6", "#005842", "#008160", "#004B8D"
    ],
    'Valves, Actuators, and Regulators': [
      "#003564", "#2A575F", "#008160", "#8732AD", "#F52190", "#319FFF",
      "#89C1CB", "#54F0C6"
    ],
    'Fluid Control & Pnuematics': [
      "#003564", "#2A575F", "#008160", "#8732AD", "#F52190", "#319FFF"
    ],
    'Control & Safety Systems': [
      "#003564", "#2A575F", "#008160", "#8732AD", "#F52190", "#319FFF",
      "#89C1CB", "#54F0C6", "#DEBFEC", "#0061B6", "#F96FB7"
    ],
    'Process Control': [
      "#003564", "#2A575F", "#008160", "#8732AD", "#F52190", "#319FFF"
    ],
    'Safety Systems': [
      "#003564", "#2A575F", "#008160", "#8732AD", "#F52190", "#319FFF",
      "#89C1CB", "#54F0C6", "#DEBFEC", "#0061B6", "#F96FB7"
    ],
    'default': [
      "#003564", "#2A575F", "#0076DF", "#E30A7C", "#89C1CB", "#950751",
      "#319FFF", "#438A98", "#6E298D", "#0061B6", "#005842", "#008160", "#004B8D"
    ]
  },
  ratings: {
    'Green': '#00805A',
    'Yellow': '#F8B11E',
    'Red': '#D31245',
    'Unrated': '#D9D9D9'
  },
  recommendations: {
    'Repair': '#339FFF',
    'Calibration / Validation / Verification': '#014C8E',
    'No Action Required': '#00805A',
    'Open / Inspect': '#F79428',
    'Replace': '#EC2257',
    'Perform Diagnostics': '#FEC944',
    'Other': '#757575'
  }
};

// Calculate the total from raw API data
function calculateTotal(data: ChartDataPoint[]): number {
  return data.reduce((sum, item) => sum + item.count, 0);
}

// Helper function to get color for device type based on its category
function getDeviceTypeColor(deviceType: string, category: string, index: number): string {
  // Get the palette for this category
  const palette = chartPalette.deviceTypePalettes[category as keyof typeof chartPalette.deviceTypePalettes] 
    || chartPalette.deviceTypePalettes.default;
  
  // Return color from palette based on index
  return palette[index % palette.length] || '#9E9E9E';
}

// Helper function to get color for category
function getCategoryColor(category: string, index: number): string {
  // First try to get from palette
  const paletteColor = chartPalette.categories[category as keyof typeof chartPalette.categories];
  if (paletteColor) return paletteColor;
  
  // Fallback to predefined colors based on index
  const fallbackColors = ['#329FFF', '#EC2257', '#F79428', '#FEC944', '#88C7FF', '#00805A'];
  return fallbackColors[index % fallbackColors.length] || '#9E9E9E';
}

// Transform nested device type data with categories
export function transformDeviceTypeData(data: DeviceTypeCategoryData[]): LifecycleStatusCardData[] {
  // Calculate total across all categories
  const total = data.reduce((sum, category) => sum + category.total_count, 0);
  
  return data.map((category, categoryIndex) => {
    // Transform device types within each category
    const children: LifecycleStatusCardData[] = category.device_types.map((deviceType, deviceIndex) => ({
      name: deviceType.device_type,
      value: deviceType.count,
      percentage: calculatePercentage(deviceType.count, category.total_count),
      color: getDeviceTypeColor(deviceType.device_type, category.category, deviceIndex),
      children: [] // Device types don't have children
    }));

    return {
      name: category.category,
      value: category.total_count,
      percentage: calculatePercentage(category.total_count, total),
      color: getCategoryColor(category.category, categoryIndex),
      children: children
    };
  });
}

export function transformRatingsData(data: ChartDataPoint[]): LifecycleStatusCardData[] {
  const total = calculateTotal(data);
  return data.map(({ name, count }) => ({
    name,
    value: count,
    percentage: calculatePercentage(count, total),
    color: chartPalette.ratings[name as keyof typeof chartPalette.ratings] || '#9E9E9E'
  }));
}

export function transformRecommendationsData(data: ChartDataPoint[]): LifecycleStatusCardData[] {
  const total = calculateTotal(data);
  return data.map(({ name, count }) => ({
    name,
    value: count,
    percentage: calculatePercentage(count, total),
    color: chartPalette.recommendations[name as keyof typeof chartPalette.recommendations] || '#9E9E9E'
  }));
}

export function calculatePercentage(value: number, total: number): string {
  if (total === 0) return '0%';
  const percentage = Math.min(100, Math.round((value / total) * 100));
  return `${percentage}%`;
}
